import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	CalendarDays,
	ChartNoAxesColumnIncreasing,
	Github,
	Layers3,
	Megaphone,
	PackageCheck,
	Repeat2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const GITHUB_REPO_URL = "https://github.com/agentreacher/skills";
const INSTALL_COMMAND = "npx skills add agentreacher/skills --all";

const DEMO_PROMPT_1 =
	"/positioning Find the market wedge for a 2-week BrandReel.com launch. BrandReel is a video marketing tool.";
const DEMO_ACTIONS_A = [
	{
		tool: "1. Map competitors",
		result: "Compare adjacent video marketing tools and their promises",
	},
	{
		tool: "2. Find the wedge",
		result: "Focus on better product videos without a bigger marketing team",
	},
	{
		tool: "3. Shape angles",
		result: "Turn the wedge into credible campaign messaging",
	},
	{
		tool: "4. Flag objections",
		result: "Prepare answers for quality, review, and brand voice concerns",
	},
];
const DEMO_FINAL_A = DEMO_ACTIONS_A.length + 1;

const skills = [
	{
		path: "/positioning",
		description: "Turns competitor gaps into an angle for your campaign.",
		outcome:
			"Maps the landscape, finds the wedge, and converts it into messaging angles for social campaigns.",
		icon: Layers3,
		href: `${GITHUB_REPO_URL}/blob/main/skills/positioning/SKILL.md`,
	},
	{
		path: "/content-pillars",
		description: "Splits your angle into 3 to 4 repeatable campaign themes.",
		outcome:
			"Creates durable pillars with post formats, proof sources, boundaries, and ratio guidance.",
		icon: Layers3,
		href: `${GITHUB_REPO_URL}/blob/main/skills/content-pillars/SKILL.md`,
	},
	{
		path: "/cadence-planner",
		description: "Plans how often you want to post.",
		outcome:
			"Turns constraints into weekly frequency, platform roles, time windows, and approval cutoffs.",
		icon: ChartNoAxesColumnIncreasing,
		href: `${GITHUB_REPO_URL}/blob/main/skills/cadence-planner/SKILL.md`,
	},
	{
		path: "/repurpose",
		description:
			"Turns your existing source material into post ideas for each theme.",
		outcome:
			"Extracts the strongest ideas from blogs, changelogs, demos, or transcripts and shapes them into a campaign package.",
		icon: Repeat2,
		href: `${GITHUB_REPO_URL}/blob/main/skills/repurpose/SKILL.md`,
	},
	{
		path: "/viral-hooks",
		description:
			"Turns each post idea into stronger openings that fit the platform.",
		outcome:
			"Turns a campaign idea into scored hooks, safer variants, sharper variants, and anti-hooks to avoid.",
		icon: Megaphone,
		href: `${GITHUB_REPO_URL}/blob/main/skills/viral-hooks/SKILL.md`,
	},
	{
		path: "/post-packager",
		description:
			"Creates post drafts with copy, asset, time, CTA, and review notes ready for one-click approval.",
		outcome:
			"Normalizes copy, timing, platform, asset, CTA, owner, status, review notes, and blockers.",
		icon: PackageCheck,
		href: `${GITHUB_REPO_URL}/blob/main/skills/post-packager/SKILL.md`,
	},
	{
		path: "/calendar-builder",
		description: "Builds a scheduling-ready publishing calendar.",
		outcome:
			"Produces scheduling-ready rows with date, time, platform, pillar, angle, asset, CTA, and status.",
		icon: CalendarDays,
		href: `${GITHUB_REPO_URL}/blob/main/skills/calendar-builder/SKILL.md`,
	},
] satisfies Array<{
	path: string;
	description: string;
	outcome: string;
	icon: typeof Megaphone;
	href: string;
}>;

export const Route = createFileRoute("/skills")({
	component: SkillsPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/skills",
		});

		return {
			meta: [
				{ title: "AgentReacher | Open-source social media agent skills" },
				{
					name: "description",
					content:
						"Open-source agent skills for positioning, content pillars, cadence planning, repurposing, social hooks, post packaging, and calendar building.",
				},
				{
					property: "og:title",
					content: "AgentReacher | Open-source social media agent skills",
				},
				{
					property: "og:description",
					content:
						"Reusable social media workflow skills for planning and scheduling a full period of content.",
				},
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentReacher" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/skills",
			}),
		};
	},
});

function SkillsPage() {
	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />

			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						{/* Install + intro */}
						<section className="overflow-hidden rounded-md bg-white dark:bg-black">
							<div className="mx-auto flex max-w-2xl flex-col items-center px-8 py-12 text-center sm:py-16">
								<h1 className="text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
									Agent skills for{" "}
									<span className="text-blue-600 dark:text-blue-400">
										social campaigns
									</span>
								</h1>
								<p
									className="mt-2 text-base text-muted-foreground"
									style={{ lineHeight: "1.6" }}
								>
									Install seven workflow skills for planning a campaign period.
									Start with positioning, then move through pillars, cadence,
									repurposing, hooks, post packaging, and a scheduling-ready
									calendar.
								</p>
								<div className="mt-6 flex flex-wrap items-center justify-center gap-3">
									<a
										href={GITHUB_REPO_URL}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
									>
										<Github className="size-4" />
										View on GitHub
									</a>
								</div>
							</div>
						</section>

						<SkillsAgentDemo />

						{/* Skills list */}
						<section className="rounded-md bg-white dark:bg-black">
							<h2 className="sr-only">Available skills</h2>
							<ul className="flex flex-col gap-px bg-border">
								{skills.map((skill) => {
									const Icon = skill.icon;
									return (
										<li key={skill.path}>
											<a
												href={skill.href}
												target="_blank"
												rel="noopener noreferrer"
												className="group flex items-start gap-4 bg-background px-6 py-5 transition-colors hover:bg-muted/30"
											>
												<div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background">
													<Icon
														className="size-4 text-foreground"
														strokeWidth={1.8}
														aria-hidden={true}
													/>
												</div>
												<div className="min-w-0 flex-1">
													<h3 className="text-base font-medium text-foreground">
														{skill.path}
													</h3>
													<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
														{skill.description}
													</p>
												</div>
												<ArrowRight className="size-4 shrink-0 self-center text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
											</a>
										</li>
									);
								})}
							</ul>
						</section>

						{/* Closing CTA */}
						<section className="rounded-md bg-white dark:bg-black px-8 py-12 sm:py-16">
							<Reveal className="mx-auto flex max-w-2xl flex-col items-center text-center">
								<h2 className="text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">
									Browse the source, fork it, send a PR.
								</h2>
								<p className="mt-3 text-sm text-muted-foreground sm:text-base">
									The repo is MIT-licensed. Open an issue if a skill is missing,
									or wire one of these into your own agent today.
								</p>
								<div className="mt-7 flex flex-wrap items-center justify-center gap-3">
									<a
										href={GITHUB_REPO_URL}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
									>
										<Github className="size-4" />
										View on GitHub
									</a>
									<Link
										to="/signup"
										className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
									>
										Start with AgentReacher
										<ArrowRight className="size-3.5" />
									</Link>
								</div>
							</Reveal>
						</section>
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

function SkillsAgentDemo() {
	const containerRef = useRef<HTMLDivElement>(null);
	const [started, setStarted] = useState(false);
	const [typedA, setTypedA] = useState(0);
	const [stepA, setStepA] = useState(0);
	const [thinkingTicks, setThinkingTicks] = useState(0);

	useEffect(() => {
		const el = containerRef.current;
		if (!el || typeof window === "undefined") return;
		const prefersReduced = window.matchMedia?.(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (prefersReduced) {
			setStarted(true);
			setTypedA(DEMO_PROMPT_1.length);
			setStepA(DEMO_FINAL_A);
			return;
		}
		if (typeof IntersectionObserver === "undefined") {
			setStarted(true);
			return;
		}
		const obs = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setStarted(true);
						obs.disconnect();
					}
				}
			},
			{ threshold: 0.3 },
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, []);

	useEffect(() => {
		if (!started || typedA >= DEMO_PROMPT_1.length) return;
		const t = setTimeout(() => setTypedA((c) => c + 1), 55);
		return () => clearTimeout(t);
	}, [started, typedA]);

	useEffect(() => {
		if (typedA < DEMO_PROMPT_1.length || stepA >= DEMO_FINAL_A) return;
		const delay = stepA === 0 ? 2800 : 650;
		const t = setTimeout(() => setStepA((s) => s + 1), delay);
		return () => clearTimeout(t);
	}, [typedA, stepA]);

	const submittedA = typedA >= DEMO_PROMPT_1.length;
	const thinkingA = submittedA && stepA === 0;
	const thinking = thinkingA;

	useEffect(() => {
		if (!thinking) {
			setThinkingTicks(0);
			return;
		}
		const id = setInterval(() => setThinkingTicks((t) => t + 1), 100);
		return () => clearInterval(id);
	}, [thinking]);

	const thinkingSeconds = Math.floor(thinkingTicks / 10);

	return (
		<section
			ref={containerRef}
			className="rounded-md bg-white dark:bg-black px-4 py-10 sm:px-8 sm:py-14"
		>
			<div className="mx-auto flex max-w-3xl flex-col items-center gap-5">
				<SetupCopyBlock prefix="$" text={INSTALL_COMMAND} className="w-fit" />
				<div className="w-full rounded-2xl border border-border bg-muted/40 p-2 sm:p-3">
					<div className="overflow-hidden rounded-xl border border-stone-800 bg-stone-950 shadow-2xl shadow-black/20 selection:bg-amber-500/40 selection:text-stone-50">
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 border-b border-stone-800/60 bg-stone-900/40 px-4 py-2.5">
							<div className="flex items-center gap-1.5">
								<span className="size-2.5 rounded-full bg-[#ff5f57]" />
								<span className="size-2.5 rounded-full bg-[#febc2e]" />
								<span className="size-2.5 rounded-full bg-[#28c840]" />
							</div>
							<span className="text-center font-mono text-[11px] text-stone-400">
								agentreacher/skills
							</span>
							<span className="text-[11px] text-stone-400">slash skills</span>
						</div>

						<div className="mx-auto flex h-[24rem] w-full max-w-2xl flex-col justify-end gap-5 overflow-hidden px-5 py-6 text-sm text-stone-100 sm:px-8 sm:py-8">
							<div className="rounded-2xl border border-stone-800 bg-stone-900/70 px-4 py-3 leading-relaxed">
								<span>{DEMO_PROMPT_1.slice(0, typedA)}</span>
								{!submittedA && (
									<span
										aria-hidden="true"
										className="ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 animate-pulse bg-stone-300 align-middle"
									/>
								)}
							</div>

							{thinkingA && (
								<p className="flex items-center gap-2 font-mono text-[12.5px] text-stone-400">
									<span className="inline-block animate-pulse text-amber-500">
										✻
									</span>
									<span>Running /content-pillars...</span>
									<span className="text-stone-400">{thinkingSeconds}s</span>
								</p>
							)}

							{submittedA && stepA >= 1 && (
								<ul className="space-y-2 font-mono text-[12.5px] leading-relaxed">
									{DEMO_ACTIONS_A.map((a, idx) =>
										stepA >= idx + 1 ? (
											<li
												key={a.tool}
												className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
											>
												<span className="text-amber-500">-</span>
												<span className="text-stone-200">{a.tool}</span>
												<span className="text-stone-400">{a.result}</span>
											</li>
										) : null,
									)}
								</ul>
							)}

							{submittedA && stepA >= DEMO_FINAL_A && (
								<p className="leading-relaxed">
									<span className="font-semibold">Next:</span> run
									/content-pillars, /cadence-planner, /repurpose, /viral-hooks,
									/post-packager, then /calendar-builder.
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
